import { SmartusComponentsModule } from '@affinis/smartus-components';
import { CommonModule } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import {
  FormArray,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  AreaRequirementViewModel,
  CostCenterViewModel,
  CreateSpaceRequestRequest,
  EmploymentClient,
  EmploymentSearchResultDto,
  RequestFileWrapperOfCreateSpaceRequestRequest,
} from '@core/api';
import { BreadcrumbService } from '@core/services/breadcrumb-service/BreadcrumbService';
import { EmployeeSearchFormlyFieldService } from '@core/services/employee-search-formly-field-service/employee-search-formly-field.service';
import { FileService } from '@core/services/file-service/file.service';
import { LocationFormlyFieldService } from '@core/services/formly-service/location-formly-field.service';
import { TicketCreateBaseComponent } from '@features/tickets/components/ticket-create/ticket-create.base';
import { MtxGridColumn, MtxGridModule } from '@ng-matero/extensions/grid';
import { FormlyModule } from '@ngx-formly/core';
import { TranslateModule } from '@ngx-translate/core';
import { OverrideModule } from '@override/override.module';
import { DocumentType } from '@shared/types/document.types';
import {
  emailcc,
  fileAttachments,
} from '../tickets/ticket-create/field-configs';

@Component({
  selector: 'app-area-request',
  standalone: true,
  imports: [
    SmartusComponentsModule,
    TranslateModule,
    FormsModule,
    ReactiveFormsModule,
    FormlyModule,
    CommonModule,
    MatDatepickerModule,
    OverrideModule,
    MatInputModule,
    MatFormFieldModule,
    MatButtonModule,
    MatSelectModule,
    MatTooltipModule,
    MtxGridModule,
  ],
  templateUrl: './area-request.component.html',
  styleUrl: './area-request.component.scss',
})
export class AreaRequestComponent
  extends TicketCreateBaseComponent
  implements OnInit
{
  private readonly employeeSearchFormlyFieldService = inject(
    EmployeeSearchFormlyFieldService
  );
  private readonly locationFormlyFieldService = inject(
    LocationFormlyFieldService
  );
  private readonly breadcrumbService = inject(BreadcrumbService);
  private readonly fileService = inject(FileService);
  private readonly employmentClient = inject(EmploymentClient);

  minDesiredDate = new Date();

  currentUserEmail = this.msalService.instance.getActiveAccount()?.idTokenClaims
    ?.email as string;

  currentUser: EmploymentSearchResultDto;

  override form: FormGroup;

  areaRequestColumns: MtxGridColumn[] = [
    {
      header: this.translateService.instant(
        'ticket.create.space_request.requested_area.quantity'
      ),
      field: 'quantity',
    },
    {
      header: this.translateService.instant(
        'ticket.create.space_request.requested_area.type'
      ),
      field: 'spaceType',
    },
    {
      header: this.translateService.instant(
        'ticket.create.space_request.requested_area.costcenter'
      ),
      field: 'costcenter',
    },
  ];

  areaRequestData = [];

  ngOnInit() {
    this.form = new FormGroup({
      areaRequest: new FormArray([]),
    });

    this.title = this.translateService.instant('Flächenanfrage');

    this.breadcrumbService.setBreadcrumbs([{ label: this.title }]);

    this.successTitle = this.translateService.instant(
      'ticket.create.success.title'
    );
    this.successMessage = this.translateService.instant(
      'ticket.create.success.message_short'
    );

    this.minDesiredDate.setDate(this.minDesiredDate.getDate() + 1);
    this.loading = true;
    this.loadingMessage = this.translateService.instant('form.loading');
    this.employmentClient.getEmployeeByEmail(this.currentUserEmail).subscribe({
      next: res => {
        this.currentUser = res;
        this.formFields = this.getFormFields();
        this.loading = false;
      },
    });

    this.card = {
      footer: {
        button1: {
          label: this.translateService.instant('forward'),
          disabled: false,
          onClick: () => {
            this.fileService.setFileAttachmentType(
              this.form.value,
              DocumentType.Sonstiges
            );
            this.validateForm();
          },
        },
      },
    };
  }

  getFormFields() {
    return [
      {
        wrappers: ['form-field'],
        key: 'shortTitle',
        type: 'input',
        props: {
          label: this.translateService.instant(
            'ticket.create.space_request.short_title'
          ),
          visibleInConfirm: true,
          required: true,
        },
      },
      {
        wrappers: ['form-field'],
        key: 'incidentDescription',
        type: 'textarea',
        props: {
          label: this.translateService.instant(
            'ticket.create.space_request.description'
          ),
          visibleInConfirm: true,
          required: true,
          autosize: true,
          autosizeMinRows: 5,
        },
      },
      {
        wrappers: [],
        fieldGroup: [
          {
            type: 'chapter',
            props: {
              chapterTitle: this.translateService.instant(
                'ticket.create.space_request.requested_location'
              ),
            },
          },
          ...this.locationFormlyFieldService.autocompleteSearch(
            false,
            null,
            true,
            null,
            null,
            null,
            this.translateService.instant(
              'location.shared.delete_location_search_tooltip'
            )
          ),
          {
            type: 'datepicker',
            key: 'desiredDate',
            props: {
              label: this.translateService.instant(
                'ticket.create.space_request.requested_date'
              ),
              datepickerOptions: {
                min: this.minDesiredDate,
              },
              required: true,
            },
          },
          {
            fieldGroup: [
              {
                type: 'chapter',
                props: {
                  chapterTitle: this.translateService.instant(
                    'ticket.create.space_request.requested_area.title'
                  ),
                },
              },
              {
                type: 'area-request',
                key: 'areaRequest',
                props: {
                  required: true,
                },
                hooks: {
                  onInit: field => {
                    field?.formControl?.valueChanges.subscribe({
                      next: () => {
                        this.areaRequestData = this.formModel['areaRequest'];
                      },
                    });
                  },
                },
              },
            ],
          },
          ...fileAttachments(this.translateService, true),
          {
            wrappers: [],
            fieldGroup: [
              {
                type: 'chapter',
                props: {
                  chapterTitle: this.translateService.instant(
                    'ticket.create.headline__search_creator'
                  ),
                },
              },
              {
                key: 'searchContact',
                fieldGroup: [
                  ...this.employeeSearchFormlyFieldService.autocompleteSearch(
                    'ticket.create.placeholder__search_contact',
                    null,
                    true,
                    true,
                    this.currentUserEmail,
                    null,
                    null,
                    this.currentUser,
                    this.translateService.instant(
                      'ticket.create.deleteContact_tooltip'
                    )
                  ),
                ],
              },
            ],
          },
          {
            fieldGroup: [
              {
                type: 'chapter',
                props: {
                  chapterTitle: this.translateService.instant(
                    'ticket.create.label__processor'
                  ),
                },
              },
              {
                key: 'contactPerson',
                fieldGroup: [
                  ...this.employeeSearchFormlyFieldService.autocompleteSearch(
                    'ticket.create.placeholder__search_processor',
                    null,
                    false,
                    true,
                    null,
                    null,
                    null,
                    null,
                    this.translateService.instant(
                      'ticket.create.deleteContact_tooltip'
                    )
                  ),
                ],
              },
            ],
          },
          ...emailcc(this.translateService, this.onEmailChange.bind(this)),
        ],
      },
    ];
  }

  public onEmailChange(emails: string) {
    this.form.get('ccemail')?.setValue(emails as never);
  }

  public override clientEndpoint = data => {
    this.loadingMessage = this.translateService.instant('form.loading_message');
    const spaceRequestModel: RequestFileWrapperOfCreateSpaceRequestRequest = {};
    spaceRequestModel.entity = data;
    spaceRequestModel.files = data.files;
    return this.incidentClient.createSpaceRequest(spaceRequestModel);
  };

  protected override prepareSubmit = data => {
    const d = data;
    const areaRequest: CreateSpaceRequestRequest = {};
    areaRequest.shortTitle = d.shortTitle;
    areaRequest.description = d.incidentDescription;
    areaRequest.locationArchObjId = d.searchResult ? d.searchResult.id : null;
    areaRequest.requestedDate = d.desiredDate;
    areaRequest.areaRequirements = this.mapAreaRequestsToAreaRequirements(
      d.areaRequest,
      d.requestId
    );
    areaRequest.contactPersonId = d.contactPerson?.searchResult?.id ?? null;
    areaRequest.mails = d.ccemail;

    return areaRequest;
  };

  public override getStatusPath() {
    return '/dashboard';
  }

  private mapAreaRequestsToAreaRequirements(
    areaRequests: any[],
    areaRequestId: string
  ): AreaRequirementViewModel[] {
    return areaRequests.map((request, index) => ({
      id: `req-${index + 1}`,
      areaRequestId: areaRequestId,
      squareMeters: request.quantity,
      costCenter: this.mapCostCenter(request.costcenter),
      type: request.spaceType,
    }));
  }

  private mapCostCenter(costCenterString: string): CostCenterViewModel {
    return {
      id: costCenterString,
      name: costCenterString,
    };
  }
}
